import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { Label } from '@ooh/ui/components/label';
import { Textarea } from '@ooh/ui/components/textarea';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { toast } from '@ooh/ui/components/sonner';
import { parseApiError } from '@ooh/contracts';
import type { Offer } from '@ooh/contracts';
import { usePublishVersion } from './api/mutations';

// ─── Constants ───────────────────────────────────────────────

const DIALOG_MAX_WIDTH = 'max-w-lg';

// ─── Types ────────────────────────────────────────────────────

export interface PublishVersionDialogProps {
  open: boolean;
  onClose: () => void;
  offerId: string;
  offer: Pick<Offer, 'updatedAt'>;
}

// ─── Error types ──────────────────────────────────────────────

type InlineError = 'stale' | 'validation' | null;

const MAX_DESCRIPTION = 500;

// ─── Component ───────────────────────────────────────────────

export function PublishVersionDialog({ open, onClose, offerId, offer }: PublishVersionDialogProps) {
  const { t } = useTranslation('offers');
  const [changeDescription, setChangeDescription] = React.useState('');
  const [inlineError, setInlineError] = React.useState<InlineError>(null);

  const publishVersion = usePublishVersion(offerId);

  // Reset form state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setChangeDescription('');
      setInlineError(null);
    }
  }, [open]);

  if (!open) return null;

  // The change note is optional: confirm is allowed even when the field is empty.
  const trimmed = changeDescription.trim();
  const canConfirm = !publishVersion.isPending;

  async function handleConfirm() {
    if (!canConfirm) return;
    setInlineError(null);

    try {
      await publishVersion.mutateAsync({
        changeDescription: trimmed.length > 0 ? trimmed : undefined,
        updatedAt: offer.updatedAt,
      });
      toast.success(t('workspace.versions.publishDialog.successToast'));
      onClose();
    } catch (error) {
      const parsed = parseApiError(error);
      if (parsed.statusCode === 409) {
        setInlineError('stale');
      } else if (parsed.statusCode === 422) {
        setInlineError('validation');
      }
      // Other errors are handled by the mutation's onError (shows generic toast)
    }
  }

  return (
    <ModalDialog
      onClose={onClose}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="publish-version-dialog"
      header={
        <h2 className="text-base font-semibold">{t('workspace.versions.publishDialog.title')}</h2>
      }
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('workspace.versions.publishDialog.cancelBtn')}
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() => void handleConfirm()}
            data-testid="publish-version-confirm-btn"
          >
            {t('workspace.versions.publishDialog.confirmBtn')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* 409 stale inline error */}
        {inlineError === 'stale' ? (
          <div
            className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="publish-version-stale-error"
          >
            {t('workspace.versions.publishDialog.staleError')}
          </div>
        ) : null}

        {/* Description textarea */}
        <div className="space-y-2">
          <Label htmlFor="publish-version-description">
            {t('workspace.versions.publishDialog.descriptionLabel')}
          </Label>
          <Textarea
            id="publish-version-description"
            maxLength={MAX_DESCRIPTION}
            placeholder={t('workspace.versions.publishDialog.descriptionPlaceholder')}
            value={changeDescription}
            onChange={(event) => {
              setChangeDescription(event.target.value);
              if (inlineError === 'validation') setInlineError(null);
            }}
            className="min-h-28 resize-none"
            error={inlineError === 'validation'}
            aria-invalid={inlineError === 'validation' || undefined}
          />
          {/* 422 field-level error */}
          {inlineError === 'validation' ? (
            <p className="text-xs text-destructive" data-testid="publish-version-validation-error">
              {t('workspace.versions.publishDialog.validationError')}
            </p>
          ) : null}
          {/* Character counter */}
          <div className="text-right text-xs text-muted-foreground">
            {t('workspace.versions.publishDialog.charCounter', {
              current: changeDescription.length,
            })}
          </div>
        </div>
      </div>
    </ModalDialog>
  );
}
